import { useMemo, useRef } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import { ProductPosition } from "shared/types/ProductPosition"
import { ProductPositionTypename } from "shared/types/Project"
import { FormRef } from "shared/types/ReactHookForm"

import { useAttachmentsByShareLinkQuery } from "entities/attachments/api/useAttachmentsByShareLinkQuery"
import {
  useProjectAttachmentCategoriesQuery,
  useProjectAttachmentsByShareLinkQuery
} from "entities/attachments/graphql/project-attachments.queries.generated"
import { useInstallationReferenceObjTransformer } from "entities/building-references/model/useInstallationReferenceObjTransformer"
import { useProductPositionInstallationReferenceObjs } from "entities/product-positions/model/useProductPositionInstallationReferenceObjs"
import { useProductPositionPrice } from "entities/product-positions/model/useProductPositionPrice"
import {
  ProductPositionFormData,
  ProductPositionFormState
} from "entities/product-positions/types/ProductPositionForm"
import { useProductConfigurationModel } from "entities/products/model/useProductConfigurationModel"
import { useVariantKey } from "entities/products/model/useVariantKey"
import { usePriceGroupOptions } from "entities/projects/model/usePriceGroupOptions"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { ProductPositionFormModel } from "features/product-position-management/types/ProductPositionFormModel"
import { ProductPositionFormOptions } from "features/product-position-management/types/ProductPositionFormOptions"
import { useProjectChildrenByShareLinkQuery } from "features/project-sharing/graphql/queries.generated"

export interface UseSharedProductPositionFormModelArgs {
  id: string
  productPosition: Partial<ProductPosition> | null
  projectShareID: string

  isLoading?: boolean

  formOptions?: ProductPositionFormOptions
}

export function useSharedProductPositionFormModel(
  args: UseSharedProductPositionFormModelArgs
): ProductPositionFormModel {
  const { productPosition, projectShareID } = args
  const formRef: FormRef<
    ProductPositionFormState,
    unknown,
    ProductPositionFormData
  > = useRef(null)

  const projectID = projectShareID

  const { price: isPriceServiceAvailable } = useAPIHealth()

  const { productType } = useVariantKey(productPosition?.variantKey || null)
  const { model, isLoading: isLoadingProductConfigurationModels } =
    useProductConfigurationModel(productType)

  const { price, isLoading: isLoadingPrice } = useProductPositionPrice({
    id: productPosition?.id,
    quantity: productPosition?.quantity,
    projectID,
    isShareID: true
  })

  const { data: projectData, loading: isLoadingSpecialPriceGroups } =
    useProjectChildrenByShareLinkQuery({ variables: { id: projectShareID } })
  const project = projectData?.projectByShareLink

  const { options: priceGroupOptions, isLoading: isLoadingPriceGroupOptions } =
    usePriceGroupOptions()

  const isSpecialPriceGroup = useMemo(() => {
    return project?.specialPriceGroups?.some(
      group => group.name === productType
    )
  }, [productType, project?.specialPriceGroups])

  const installationReferenceIDs = useMemo(
    () => productPosition?.references?.map(reference => reference.permalink),
    [productPosition?.references]
  )

  const { installationReferenceObjs } =
    useProductPositionInstallationReferenceObjs({
      installationReferenceIDs
    })

  const installationReferences = useInstallationReferenceObjTransformer({
    installationReferenceObjs
  })

  const isLoadingInstallationReferences =
    !!productPosition?.references &&
    productPosition?.references.length > 0 &&
    installationReferences === undefined

  const { data: linkedAttachmentsData } = useAttachmentsByShareLinkQuery({
    id: productPosition?.id ?? "",
    resourceType: ProductPositionTypename,
    projectShareID,
    skip: !productPosition?.id
  })

  const { data: attachmentsData } = useProjectAttachmentsByShareLinkQuery({
    variables: { id: projectShareID },
    skip: !productPosition?.id
  })

  const { data: attachmentCategoriesData } =
    useProjectAttachmentCategoriesQuery()

  return {
    hasProductConfigurationModel: !!model,
    isLoadingProductConfigurationModels,
    price,
    isLoadingPrice,
    isSpecialPriceGroup,
    isLoadingSpecialPriceGroups,
    isPriceServiceAvailable,
    installationReferences,
    isLoadingInstallationReferences,
    formRef,
    permissions: readonlyProjectPermissions,

    isLoadingPriceGroupOptions,
    priceGroupOptions,

    attachments: attachmentsData?.attachments ?? null,
    linkedAttachments: linkedAttachmentsData?.attachmentsByShareLink ?? null,
    attachmentCategories:
      attachmentCategoriesData?.projectAttachmentCategories.categories ?? null,

    ...args,

    defaultProjectID: args.projectShareID,
    defaultParentID: args.projectShareID,
    isDefaultEditing: false,
    productPosition
  }
}
